import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import withSessionCheck from '../../../withSessionCheck';
import {
  Paper, Fade, CircularProgress, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, IconButton, Tooltip
} from '@mui/material';

const PharmacyDispense = () => {
  const history = useHistory();
  const { id } = useParams();
  const { isAuthenticated, companyId, token, userId, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [banner, setBanner] = useState({ show: false, message: '', type: '' });
  const [entryDetails, setEntryDetails] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(1);
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    fetchEntryDetails();
  }, [isAuthenticated, id, token, logout]);

  const showBanner = (message, type = 'info') => {
    setBanner({ show: true, message, type });
    setTimeout(() => {
      setBanner({ show: false, message: '', type: '' });
    }, 4000);
  };

  const [formData, setFormData] = useState({
    CompanyId: companyId,
    CreatedBy: userId,
  });

  const fetchEntryDetails = async () => {
    setLoading(true);
    try {
      const response = await fetchData('DoctorEntryApi', `/${id}`, token);
      if (response.responseCode === '1111') {
        setEntryDetails(response.response_data);

        // Set main form data
        setFormData({
          CompanyId: companyId,
          ModifiedBy: userId,
          Id: response.response_data.DoctorEntry.Id,
          AppointmentId: response.response_data.DoctorEntry.AppointmentId,
          DoctorId: response.response_data.DoctorEntry.DoctorId || '',
          PatientId: response.response_data.DoctorEntry.PatientId || '',
        });

        // Initialize medicines from prescriptions
        const prescriptions = response.response_data.PrescriptionDetails || [];
        const initialMedicines = prescriptions.map(pres => ({
          id: pres.Id,
          itemId: pres.ItemId,
          medicineName: pres.MedicineName,
          dosage: pres.Dosage,
          frequency: pres.Frequency,
          duration: pres.Duration,
          quantity: 1,
          unitPrice: 0,
          totalPrice: 0,
          selected: true
        }));
        setMedicines(initialMedicines);
      } else {
        showBanner('Failed to load entry details', 'error');
      }
    } catch (error) {
      console.error('Error fetching entry details:', error);
      showBanner('Failed to load entry details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMedicineChange = (index, field, value) => {
    const updatedMedicines = [...medicines];
    updatedMedicines[index][field] = value;

    // Calculate total price
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = parseFloat(updatedMedicines[index].quantity) || 0;
      const unitPrice = parseFloat(updatedMedicines[index].unitPrice) || 0;
      updatedMedicines[index].totalPrice = quantity * unitPrice;
    }

    setMedicines(updatedMedicines);
    calculateTotalPayment(updatedMedicines);
  };

  const toggleMedicineSelection = (index) => {
    const updatedMedicines = [...medicines];
    updatedMedicines[index].selected = !updatedMedicines[index].selected;
    setMedicines(updatedMedicines);
    calculateTotalPayment(updatedMedicines);
  };

  const calculateTotalPayment = (medicineList) => {
    const total = medicineList
      .filter(med => med.selected)
      .reduce((sum, med) => sum + (med.totalPrice || 0), 0);
    setPaymentAmount(total);
  };

  const handleSaveDispense = async () => {
    const selectedMedicines = medicines.filter(med => med.selected);

    if (selectedMedicines.length === 0) {
      showBanner('Please select at least one medicine to dispense', 'error');
      return;
    }

    if (paymentAmount <= 0) {
      showBanner('Please enter valid prices for selected medicines', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {

        MedicineDispensing: {
          CompanyId: companyId,
          AppointmentId: formData.AppointmentId,
          PatientId: formData.PatientId,
          DoctorId: formData.DoctorId,
          DoctorEntryId: formData.Id,
          BillAmount: paymentAmount,
          PaymentTypeId: paymentMethod,
          CreatedBy: userId,
          Remarks: remarks,
        },
        MedicineDispensingDetails: selectedMedicines.map(med => ({
          ItemId: med.itemId,
          MedicineName: med.medicineName,
          Quantity: med.quantity,
          Rate: med.unitPrice,
          Amount: med.totalPrice,
          GSTRate: 0,
          GSTAmount: 0,
          TotalAmount: med.totalPrice,
        })),
      };

      const response = await postData('MedicineDispensingApi', payload, token);

      if (response.responseCode === '1111') {
        showBanner('Medicine dispensed and payment collected successfully', 'success');
        setTimeout(() => {
          history.push('/PharmacyIndex');
        }, 2000);
      } else {
        showBanner(response.responseMessage || 'Failed to save dispense record', 'error');
      }
    } catch (error) {
      console.error('Error saving dispense:', error);
      showBanner('Failed to save dispense record', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <CircularProgress style={{ color: '#10b981', width: '48px', height: '48px' }} />
        <p style={{ marginTop: '20px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>Loading entry details...</p>
      </div>
    );
  }

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ padding: '10px', maxWidth: '100%' }}>
        {/* Banner Notification */}
        {banner.show && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            minWidth: '300px',
            maxWidth: '500px',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.4s ease-out',
            background: banner.type === 'success'
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : banner.type === 'error'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white'
          }}>
            <span style={{ fontSize: '24px' }}>
              {banner.type === 'success' ? '✅' : banner.type === 'error' ? '❌' : 'ℹ️'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                {banner.type === 'success' ? 'Success' : banner.type === 'error' ? 'Error' : 'Info'}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.95 }}>{banner.message}</div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(100px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          marginTop: '-30px'
        }}>
          {/* Header - Compact */}
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '22px' }}>💊</span>
              <div>
                <h1 style={{ fontSize: '14px', fontWeight: '700', color: 'white', margin: 0, lineHeight: '1.2' }}>
                  Medicine Dispensing & Payment
                </h1>
                <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0', lineHeight: '1.2' }}>
                  Select medicines to dispense and collect payment
                </p>
              </div>
            </div>

            <button
              onClick={() => history.push('/PharmacyIndex')}
              style={{
                padding: '5px 14px',
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              ← Back to List
            </button>
          </div>

          <div style={{ padding: '12px' }}>
            {/* Patient Information & Payment Section Combined */}
            {entryDetails && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '10px', marginBottom: '12px' }}>
                {/* Patient Information - Ultra Compact */}
                <Paper
                  elevation={0}
                  style={{
                    padding: '6px 8px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e0e7ff',
                    borderRadius: '6px',
                    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.08)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '60px',
                    height: '60px',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'translate(50%, -50%)'
                  }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', position: 'relative' }}>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      boxShadow: '0 2px 4px rgba(99, 102, 241, 0.3)'
                    }}>
                      👤
                    </div>
                    <h3 style={{ fontSize: '11px', fontWeight: '700', color: '#4f46e5', margin: 0, letterSpacing: '0.2px', textTransform: 'uppercase' }}>
                      Patient Information
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', position: 'relative' }}>
                    <div style={{
                      padding: '3px 5px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '4px',
                      border: '1px solid rgba(99, 102, 241, 0.1)'
                    }}>
                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Patient Name</span>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', marginTop: '2px' }}>
                        {entryDetails.DoctorEntry?.PatientName || 'N/A'}
                      </div>
                    </div>
                    <div style={{
                      padding: '3px 5px',
                      background: 'rgba(16, 185, 129, 0.08)',
                      borderRadius: '4px',
                      border: '1px solid rgba(16, 185, 129, 0.2)'
                    }}>
                      <span style={{ fontSize: '10px', color: '#059669', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>MRD Number</span>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#10b981', marginTop: '2px' }}>
                        {entryDetails.DoctorEntry?.MRDNo || 'N/A'}
                      </div>
                    </div>
                    <div style={{
                      padding: '3px 5px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '4px',
                      border: '1px solid rgba(99, 102, 241, 0.1)'
                    }}>
                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Doctor</span>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', marginTop: '2px' }}>
                        {entryDetails.DoctorEntry?.DoctorName || 'N/A'}
                      </div>
                    </div>
                    <div style={{
                      padding: '3px 5px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '4px',
                      border: '1px solid rgba(99, 102, 241, 0.1)'
                    }}>
                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Entry Date</span>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', marginTop: '2px' }}>
                        {formatDate(entryDetails.DoctorEntry?.DoctorEntryDate)}
                      </div>
                    </div>
                  </div>
                </Paper>

                {/* Payment Details - Ultra Compact */}
                <Paper
                  elevation={0}
                  style={{
                    padding: '6px 8px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
                    border: '1px solid #d1fae5',
                    borderRadius: '6px',
                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.12)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '50px',
                    height: '50px',
                    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'translate(-50%, -50%)'
                  }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', position: 'relative' }}>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                    }}>
                      💰
                    </div>
                    <h3 style={{ fontSize: '12px', fontWeight: '700', color: '#059669', margin: 0, letterSpacing: '0.2px', textTransform: 'uppercase' }}>
                      Payment Details
                    </h3>
                  </div>

                  <div style={{ marginBottom: '4px', position: 'relative' }}>
                    <label style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Payment Method
                    </label>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {[
                        { value: 'Cash', emoji: '💵', color: '#10b981', name: 'Cash', id: 1 },
                        { value: 'UPI', emoji: '📱', color: '#8b5cf6', name: 'UPI', id: 2 },
                        { value: 'Card', emoji: '💳', color: '#3b82f6', name: 'Card', id: 3 }
                      ].map((method) => (
                        <button
                          key={method.value}
                          onClick={() => setPaymentMethod(method.id)}
                          title={method.name}
                          style={{
                            flex: '1',
                            padding: '3px 2px',
                            background: paymentMethod === method.id
                              ? `linear-gradient(135deg, ${method.color} 0%, ${method.color}dd 100%)`
                              : 'rgba(255, 255, 255, 0.9)',
                            border: paymentMethod === method.id
                              ? `2px solid ${method.color}`
                              : '2px solid rgba(0, 0, 0, 0.08)',
                            borderRadius: '4px',
                            fontSize: '18px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: paymentMethod === method.id
                              ? `0 3px 8px ${method.color}40, 0 2px 4px ${method.color}30`
                              : '0 2px 3px rgba(0, 0, 0, 0.06)',
                            transform: paymentMethod === method.id ? 'translateY(-1px) scale(1.02)' : 'translateY(0) scale(1)',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (paymentMethod !== method.id) {
                              e.target.style.borderColor = method.color;
                              e.target.style.transform = 'translateY(-1px) scale(1.03)';
                              e.target.style.boxShadow = `0 3px 8px ${method.color}30`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (paymentMethod !== method.id) {
                              e.target.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                              e.target.style.transform = 'translateY(0) scale(1)';
                              e.target.style.boxShadow = '0 2px 3px rgba(0, 0, 0, 0.06)';
                            }
                          }}
                        >
                          {method.emoji}
                          {paymentMethod === method.id && (
                            <div style={{
                              position: 'absolute',
                              top: '-3px',
                              right: '-3px',
                              width: '14px',
                              height: '14px',
                              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '9px',
                              fontWeight: '900',
                              color: 'white',
                              boxShadow: '0 2px 6px rgba(251, 191, 36, 0.4)',
                              border: '1px solid white'
                            }}>
                              ✓
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ position: 'relative' }}>
                    <label style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Total Amount
                    </label>
                    <div style={{
                      padding: '6px 8px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '18px',
                      fontWeight: '800',
                      textAlign: 'center',
                      boxShadow: '0 3px 10px rgba(16, 185, 129, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%)'
                      }} />
                      <span style={{ position: 'relative', textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}>
                        ₹{paymentAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </Paper>
              </div>
            )}

            {/* Medicines Table - Ultra Compact */}
            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                <div style={{
                  width: '22px',
                  height: '22px',
                  background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  boxShadow: '0 2px 6px rgba(236, 72, 153, 0.3)'
                }}>
                  💊
                </div>
                <h3 style={{ fontSize: '10px', fontWeight: '700', color: '#db2777', margin: 0, letterSpacing: '0.2px', textTransform: 'uppercase' }}>
                  Prescribed Medicines
                </h3>
              </div>
              <TableContainer
                component={Paper}
                elevation={0}
                style={{
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                  borderRadius: '12px',
                  border: '1px solid #f3e8ff',
                  overflow: 'hidden'
                }}>
                <Table>
                  <TableHead>
                    <TableRow style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '12px', padding: '4px 6px', width: '40px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Select</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '12px', padding: '4px 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Medicine</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '12px', padding: '4px 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dosage</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '12px', padding: '4px 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Frequency</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '12px', padding: '4px 6px', width: '100px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quantity</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '12px', padding: '4px 6px', width: '120px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Unit Price</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '12px', padding: '4px 6px', width: '120px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medicines.map((medicine, index) => (
                      <TableRow key={index} style={{ background: medicine.selected ? '#f0fdf4' : '#ffffff' }}>
                        <TableCell style={{ padding: '4px 6px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={medicine.selected}
                            onChange={() => toggleMedicineSelection(index)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 6px', fontSize: '12px', fontWeight: '600', color: '#0f172a' }}>
                          {medicine.medicineName}
                        </TableCell>
                        <TableCell style={{ padding: '4px 6px', fontSize: '11px', color: '#475569' }}>
                          {medicine.dosage || '-'}
                        </TableCell>
                        <TableCell style={{ padding: '4px 6px', fontSize: '11px', color: '#475569' }}>
                          {medicine.frequency || '-'}
                        </TableCell>
                        <TableCell style={{ padding: '4px 6px' }}>
                          <TextField
                            type="number"
                            value={medicine.quantity}
                            onChange={(e) => handleMedicineChange(index, 'quantity', e.target.value)}
                            disabled={!medicine.selected}
                            size="small"
                            inputProps={{ min: 1, style: { fontSize: '11px', padding: '4px', textAlign: 'right' } }}
                            style={{ width: '70px' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 6px' }}>
                          <TextField
                            type="number"
                            value={medicine.unitPrice}
                            onChange={(e) => handleMedicineChange(index, 'unitPrice', e.target.value)}
                            disabled={!medicine.selected}
                            size="small"
                            inputProps={{ min: 0, step: 0.01, style: { fontSize: '11px', padding: '4px', textAlign: 'right' } }}
                            style={{ width: '90px' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 6px', fontSize: '12px', fontWeight: '700', color: '#10b981', textAlign: 'right' }}>
                          ₹{medicine.totalPrice.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            {/* Remarks and Action Buttons - Advanced Style */}
            <Paper
              elevation={0}
              style={{
                padding: '14px 16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)',
                border: '1px solid #fde68a',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.12)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '200px',
                height: '200px',
                background: 'radial-gradient(circle, rgba(245, 158, 11, 0.08) 0%, transparent 70%)',
                borderRadius: '50%',
                transform: 'translate(50%, 50%)'
              }} />

              <div style={{ marginBottom: '12px', position: 'relative' }}>
                <label style={{ fontSize: '10px', fontWeight: '600', color: '#92400e', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  📝 Remarks (Optional)
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add any additional notes..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #fbbf24',
                    borderRadius: '10px',
                    fontSize: '11px',
                    outline: 'none',
                    minHeight: '50px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    background: 'rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 6px rgba(245, 158, 11, 0.1)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#f59e0b';
                    e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#fbbf24';
                    e.target.style.boxShadow = '0 2px 6px rgba(245, 158, 11, 0.1)';
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', position: 'relative' }}>
                <button
                  onClick={() => history.push('/PharmacyIndex')}
                  style={{
                    padding: '10px 24px',
                    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                    color: '#374151',
                    border: '2px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                    letterSpacing: '0.3px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.08)';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  ← Cancel
                </button>
                <button
                  onClick={handleSaveDispense}
                  disabled={saving || paymentAmount <= 0}
                  style={{
                    padding: '10px 28px',
                    background: saving || paymentAmount <= 0
                      ? 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: saving || paymentAmount <= 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: saving || paymentAmount <= 0
                      ? '0 2px 6px rgba(0, 0, 0, 0.1)'
                      : '0 6px 16px rgba(16, 185, 129, 0.35)',
                    letterSpacing: '0.3px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving && paymentAmount > 0) {
                      e.target.style.transform = 'translateY(-2px) scale(1.02)';
                      e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.45)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving && paymentAmount > 0) {
                      e.target.style.transform = 'translateY(0) scale(1)';
                      e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.35)';
                    }
                  }}
                >
                  {!(saving || paymentAmount <= 0) && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)'
                    }} />
                  )}
                  {saving ? (
                    <>
                      <CircularProgress size={16} style={{ color: 'white' }} />
                      <span style={{ position: 'relative' }}>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '16px' }}>💊</span>
                      <span style={{ position: 'relative' }}>Dispense & Collect Payment</span>
                    </>
                  )}
                </button>
              </div>
            </Paper>
          </div>
        </div>
      </div>
    </Fade>
  );
};

export default withSessionCheck(PharmacyDispense);
